import { useNavigate } from "react-router";
import SuperTokens from "supertokens-web-js";
import * as SuperTokensSession from "supertokens-web-js/recipe/session";
import * as SuperTokensThirdPartyPasswordless from "supertokens-web-js/recipe/thirdpartypasswordless";
import * as SuperTokensThirdPartyEmailPassword from "supertokens-web-js/recipe/thirdpartyemailpassword";

import { Sentry } from "@shared/utils/init-tracking";
import { useAuthStore } from "@shared/features/auth/stores/auth";
import { useNewUserStore } from "@shared/features/newUser/stores/newUser";
import { useApi } from "@shared/features/api/hooks/useApi";
import { config } from "@shared/config";
import { defaultHeaders } from "@shared/features/api/utils";
import { useSeald } from "@shared/features/seald/hooks/use-seald";

import { useUser } from "./useUser";
import { CrossPlatformCookieHandler } from "../utils/cross-cookie-handler";
import { LoginType } from "../constants/login-type";

declare global {
  interface Window {
    logout?: () => Promise<void>;
  }
}

let isSuperTokensInit = false;

export const useAuth = () => {
  const store = useAuthStore();
  const user = useUser();
  const api = useApi();
  const seald = useSeald();
  const navigate = useNavigate();
  const newUserStore = useNewUserStore();

  const preAPIHook = async (context: any) => {
    const url = new URL(context.url);

    Object.entries(context.userContext ?? {}).forEach(([key, value]) => {
      url.searchParams.append(key, String(value));
    });

    return {
      ...context,
      url: url.toString(),
      requestInit: {
        ...context.requestInit,
        headers: {
          ...context.requestInit.headers,
          ...api.defaultHeaders,
        },
      },
    };
  };

  const init = async () => {
    if (!isSuperTokensInit) {
      SuperTokens.init({
        enableDebugLogs: false,
        appInfo: {
          apiBasePath: "/auth",
          apiDomain: api.baseUrl,
          appName: defaultHeaders()["X-Application-Name"],
        },
        clientType: "web",
        cookieHandler: CrossPlatformCookieHandler,
        recipeList: [
          SuperTokensThirdPartyPasswordless.init({
            preAPIHook,
          }),
          SuperTokensThirdPartyEmailPassword.init({
            preAPIHook,
          }),
          SuperTokensSession.init({
            preAPIHook,
            tokenTransferMethod: "header",
            onHandleEvent: (event) => {
              if (
                event.action === "UNAUTHORISED" &&
                event.sessionExpiredOrRevoked
              ) {
                const currentPath =
                  window.location.pathname + window.location.search;

                navigate(`/login?returnTo=${encodeURIComponent(currentPath)}`);
              }
            },
          }),
        ],
      });

      isSuperTokensInit = true;
    }
  };

  const ensureUserIsLoggedIn = async () => {
    try {
      await SuperTokensSession.getUserId();

      if (window.location.pathname === "/login") {
        navigate("/app");
      }

      return true;
    } catch (err) {
      if (window.location.pathname.startsWith("/app")) {
        const currentPath = window.location.pathname + window.location.search;

        navigate(`/login?returnTo=${encodeURIComponent(currentPath)}`);
      }

      return false;
    }
  };

  const createOTP = async (signType: LoginType, data: string) => {
    if (![LoginType.Email, LoginType.Phone].includes(signType)) {
      throw new Error("signType must be one of [email, phoneNumber]");
    }

    try {
      await SuperTokensThirdPartyPasswordless.createPasswordlessCode({
        [signType]: data,
      } as { email: string } | { phoneNumber: string });

      return true;
    } catch (err: any) {
      if (err.isSuperTokensGeneralError === true) {
        window.alert(err.message);
      } else {
        window.alert("Oops! Something went wrong.");
        Sentry.captureException(err, { tags: { action: "createOTP" } });
      }

      throw err;
    }
  };

  const consumeOTP = async (otp: string) => {
    const response =
      await SuperTokensThirdPartyPasswordless.consumePasswordlessCode({
        userInputCode: otp,
      });

    if (response.status === "OK") {
      try {
        await getUserData();

        return true;
      } catch (err: any) {
        if (err.response?.status === 404) {
          newUserStore.setIsAlreadyRegistered(false);

          return navigate(`/new-user${window.location.search}`);
        }

        throw err;
      }
    } else {
      const err = new Error(response.status) as any;

      err.response = response;

      Sentry.captureException(err, { tags: { action: "consumeOTP" } });

      throw err;
    }
  };

  const logout = async () => {
    let logoutUrl = "/";

    try {
      const session = await getSession();

      if (session.integrations.psc) {
        logoutUrl =
          await SuperTokensThirdPartyPasswordless.getThirdPartyAuthorisationURLWithQueryParamsAndSetState(
            {
              thirdPartyId: "psc",
              frontendRedirectURI: `${config.VITE_AUTH_CALLBACK}/psc/logout`,
              redirectURIOnProviderDashboard: `${config.VITE_AUTH_CALLBACK}/psc/logout`,
              userContext: {
                action: "logout",
                idToken: session.integrations.psc.idToken,
              },
            }
          );
      }
    } finally {
      await SuperTokensSession.signOut();
      await seald.sealdSDKInstance?.dropDatabase();
      await seald.sealdSDKInstance?.utils.cleanEncryptionSessionCache();
      store.reset();
      Sentry.setUser(null);
      window.location.assign(logoutUrl);
    }
  };

  window.logout = logout;

  const getUserData = async () => {
    const payload = await SuperTokensSession.getAccessTokenPayloadSecurely();

    if (
      payload.configMode &&
      config.VITE_CONFIG_MODE === "PROD" &&
      payload.configMode !== config.mode
    ) {
      config.mode = payload.configMode;

      return;
    }

    const data = await api.user.getCurrentUser();

    // Only the technical id: no e-mail, name or phone in Sentry.
    Sentry.setUser({ id: data.id });
    Sentry.setTag("userType", data.userType);

    store.setSession(payload);
    store.setUser(data);

    return data;
  };

  const createAdminUserBasedOnSession = async () => {
    const payload = await SuperTokensSession.getAccessTokenPayloadSecurely();

    const data = {
      id: payload.sub,
      username: payload.email,
      email: payload.email,
      userType: "admin",
    };

    try {
      await api.user.create({ user: data });
      await getUserData();
    } catch (error) {
      console.error(error);
    }

    return data;
  };

  const getSession = async () => {
    const payload = await SuperTokensSession.getAccessTokenPayloadSecurely();

    store.setSession(payload);

    return payload;
  };

  const getUserId = async () => {
    try {
      return await SuperTokensSession.getUserId();
    } catch {
      return null;
    }
  };

  const thirdParties = {
    ordoclic: async () => {
      try {
        const authUrl =
          await SuperTokensThirdPartyPasswordless.getThirdPartyAuthorisationURLWithQueryParamsAndSetState(
            {
              thirdPartyId: "ordoclic",
              frontendRedirectURI: config.VITE_AUTH_CALLBACK,
              redirectURIOnProviderDashboard: config.VITE_AUTH_CALLBACK,
            }
          );

        window.location.assign(authUrl);
      } catch (err: any) {
        console.log(err);
        if (err.isSuperTokensGeneralError === true) {
          window.alert(err.message);
        } else {
          window.alert("Oops! Something went wrong.");
        }
      }
    },

    psc: async () => {
      try {
        const authUrl =
          await SuperTokensThirdPartyPasswordless.getThirdPartyAuthorisationURLWithQueryParamsAndSetState(
            {
              thirdPartyId: "psc",
              frontendRedirectURI: `${config.VITE_AUTH_CALLBACK}/psc`,
              redirectURIOnProviderDashboard: `${config.VITE_AUTH_CALLBACK}/psc`,
            }
          );

        window.location.assign(authUrl);
      } catch (err: any) {
        console.log(err);
        if (err.isSuperTokensGeneralError === true) {
          window.alert(err.message);
        } else {
          window.alert("Oops! Something went wrong.");
        }
      }
    },
  };

  return {
    consumeOTP,
    createOTP,
    ensureUserIsLoggedIn,
    getUserData,
    getUserId,
    getSession,
    init,
    thirdParties,
    isSignIn: async () => {
      return !!(await getUserId());
    },
    logout,
    session: store.session,
    store,
    user,
    createAdminUserBasedOnSession,
  };
};
